import { MediaImageStudioMode } from '@wix/media-image-studio-opener/dist/src';
import React, { FC, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { NavLink } from 'react-router-dom';
import { Text, TextButton, WixIcons } from '../../../../../components/common';
import { ActionName } from '../../../../../constants/Bi';
import { ContextMenuId } from '../../../../../constants/ContextMenuId';
import { ModalName } from '../../../../../constants/ModalName';
import { MainSource } from '../../../../../constants/Source';
import { useMMGRSettings } from '../../../../../utils/hoc/withMMGRSettings';
import { useTrackActions } from '../../../../../utils/hooks/bi/useTrackActions';
import { ActionClickOrigin } from '../../../../../utils/hooks/bi/useTrackActions/useTrackActions.types';
import {
  TrackBoardActionsMenuClickOrigin,
  TrackBoardActionsMenuTarget,
  useTrackBoardAnalytics,
} from '../../../../../utils/hooks/bi/useTrackBoardAnalytics';
import {
  useTrackCreateVideoToolClick,
  VideoCreationScript,
} from '../../../../../utils/hooks/bi/useTrackCreateVideoToolClick';
import {
  ImageEditToolClickOrigin,
  ImageEditToolTarget,
  useTrackImageEditToolClick,
} from '../../../../../utils/hooks/bi/useTrackImageEditToolClick';
import { useTrackInfoPanelFileActions } from '../../../../../utils/hooks/bi/useTrackInfoPanelFileActions';
import { ClickOrigin as ClickOriginLearnMore } from '../../../../../utils/hooks/bi/useTrackLearnMoreClickAnalytics';
import {
  TagsActionsClickOrigins,
  TagsActionsTargets,
  useTrackTagsActions,
} from '../../../../../utils/hooks/bi/useTrackTagsActions';
import { ClickOrigin, useTrackTrashActions } from '../../../../../utils/hooks/bi/useTrackTrashActions';
import { useTrackTrashButtons } from '../../../../../utils/hooks/bi/useTrackTrashButtons';
import { useMediaStudio } from '../../../../../utils/hooks/useMediaStudio';
import { useModalHandlersAndOptions } from '../../../../../utils/hooks/useModal';
import {
  usePermanentDeleteModalHandlers,
  useShouldShowPermanentDelete,
} from '../../../../../utils/hooks/useModal/modals/usePermanentDeleteModal';
import { useSources } from '../../../../../utils/hooks/useSources';
import { useVideoMaker } from '../../../../../utils/hooks/useVideoMaker';
import { useCanUseItemsInVideoMaker } from '../../../../../utils/hooks/useVideoMaker/useVideoMaker';
import {
  MediaManagerSDKBoard,
  MediaManagerSDKFile,
  MediaManagerSDKFolder,
} from '../../../../../utils/media-manager-sdk';
import { isBoard, isSystemBoard, isUserOwnerOfBoard } from '../../../../../utils/media-manager-sdk/boards';
import {
  canEditItemInVideoMaker,
  canOpenItemInAdobeExpress,
  canOpenItemInImageEditor,
  hasLabels,
  isFile,
  isMobileFolder,
  isPurchasedFolder,
  isWixVideoLibraryLink,
  validateFileName,
} from '../../../../../utils/media-manager-sdk/items';
import { isTranscoding } from '../../../../../utils/media-manager-sdk/transcoding';
import { RouteBuilder } from '../../../../../utils/RouteHelpers';
import { PanelSection } from '../../PanelSection';
import { ActionItem } from '../ActionItem';
import { Actions } from '../Actions';
import { BoardInfo } from '../BoardInfo';
import { DeleteFlowPanelInformation } from '../DeleteFlowPanelInformation';
import { EditingItemName } from '../EditingItemName';
import { ItemInfoTable } from '../ItemInfoTable';
import { Separator } from '../Separator';
import { Team } from '../Team';
import { LabelsEditor } from './LabelsEditor';
import styles from './SingleItemBottomPart.scss';
import { IOuter } from './SingleItemBottomPart.types';
import { useAdobeExpress } from '../../../../../utils/hooks/useAdobeExpress/useAdobeExpress';
import { AdobeOpenClickSource, useAdobeExpressBI } from '../../../../../utils/hooks/useAdobeExpress/useAdobeExpressBI';
import { useFileAwarenessModalHandlers } from '../../../../../utils/hooks/useModal/modals/useFileAwarenessModal';

export const SingleItemBottomPart: FC<IOuter> = ({
  item,
  onLabelsSave,
  updateItem,
  onLabelClick,
  isMoreActionsVisible,
  restoreItems,
  isRestoreVisible,
  isDeleteForeverVisible,
  currentFolder,
  source,
  onRemoveItemsFromBoard,
}) => {
  const { isTrash, isBoards } = useSources({ source });
  const { mmgrSettings } = useMMGRSettings();
  const { team, isFileAwarenessEnabled, isHideAdobeExpressEnabled, hideCreateAndEditActions, disableTeams } =
    mmgrSettings;
  const shouldDisableEditActions = isTranscoding(item) || hideCreateAndEditActions;

  const canDisplayEditUnsplashAction = canOpenItemInImageEditor(item);

  const { trackTagsActions } = useTrackTagsActions();

  const handleLabelClick = useCallback(
    (label) => {
      onLabelClick(label);
      trackTagsActions(
        {
          clickOrigin: TagsActionsClickOrigins.MoreInfo,
          tag: label,
          target: TagsActionsTargets.Clicked,
        },
        item,
      );
    },
    [onLabelClick, trackTagsActions, item],
  );

  const { openMediaStudio } = useMediaStudio();
  const editItemInCropAndRotateMode = useCallback(
    () =>
      openMediaStudio({
        file: item as MediaManagerSDKFile,
        mode: MediaImageStudioMode.Transform,
        targetFolder: currentFolder,
      }),
    [openMediaStudio, item, currentFolder],
  );
  const editItemInAdjustMode = useCallback(
    () =>
      openMediaStudio({
        file: item as MediaManagerSDKFile,
        mode: MediaImageStudioMode.Adjust,
        targetFolder: currentFolder,
      }),
    [openMediaStudio, item, currentFolder],
  );
  const editItemInCutoutMode = useCallback(
    () =>
      openMediaStudio({
        file: item as MediaManagerSDKFile,
        mode: MediaImageStudioMode.Cutout,
        targetFolder: currentFolder,
      }),
    [openMediaStudio, item, currentFolder],
  );

  const { trackActionsDone, setLastActionParams } = useTrackActions();
  const onRenameComplete = useCallback(
    (editingName) => {
      const validFileName = validateFileName(editingName);
      if (validFileName && validFileName !== item.name) {
        updateItem(item, { name: validFileName });
      }

      setLastActionParams([item], { clickOrigin: ActionClickOrigin.FileTitle });
      trackActionsDone([item], ActionName.Rename);
    },
    [item, setLastActionParams, trackActionsDone],
  );

  const { trackInfoPanelFileAction } = useTrackInfoPanelFileActions();
  const { trackImageEditToolClick } = useTrackImageEditToolClick();

  const handleCropAndRotateClick = useCallback(() => {
    trackInfoPanelFileAction(item, ActionName.CropEdit);
    trackImageEditToolClick(
      ImageEditToolTarget.CropAndRotate,
      ImageEditToolClickOrigin.InfoPanel,
      item as MediaManagerSDKFile,
    );
    editItemInCropAndRotateMode();
  }, [editItemInCropAndRotateMode, trackInfoPanelFileAction, item]);

  const handleAdjustClick = useCallback(() => {
    trackInfoPanelFileAction(item, ActionName.Adjust);
    trackImageEditToolClick(
      ImageEditToolTarget.Adjust,
      ImageEditToolClickOrigin.InfoPanel,
      item as MediaManagerSDKFile,
    );
    editItemInAdjustMode();
  }, [editItemInAdjustMode, trackInfoPanelFileAction, item]);

  const handleCutOutClick = useCallback(() => {
    trackInfoPanelFileAction(item, ActionName.CutOut);
    trackImageEditToolClick(
      ImageEditToolTarget.ChangeBackground,
      ImageEditToolClickOrigin.InfoPanel,
      item as MediaManagerSDKFile,
    );
    editItemInCutoutMode();
  }, [trackImageEditToolClick, trackInfoPanelFileAction, editItemInCutoutMode, item]);

  const { editImageInAdobe } = useAdobeExpress(currentFolder);
  const { trackAdobeOpenClick } = useAdobeExpressBI();
  const handleEditInAdobe = useCallback(() => {
    trackAdobeOpenClick(item as MediaManagerSDKFile, AdobeOpenClickSource.ActionsPanel);
    trackInfoPanelFileAction(item, ActionName.EditInAdobeExpress);
    trackImageEditToolClick(
      ImageEditToolTarget.AdobeExpress,
      ImageEditToolClickOrigin.InfoPanel,
      item as MediaManagerSDKFile,
    );
    editImageInAdobe(item as MediaManagerSDKFile);
  }, [editImageInAdobe, item, trackImageEditToolClick, trackInfoPanelFileAction, trackAdobeOpenClick]);

  const { trackDeleteForeverButtonClicked, trackRestoreButtonClicked } = useTrackTrashButtons();
  const { trackRestore } = useTrackTrashActions();

  const { openModal: openRemoveFilesModal } = usePermanentDeleteModalHandlers();
  const { openModal: openFileAwarenessModal } = useFileAwarenessModalHandlers();

  const handleDeleteForeverButtonClick = useCallback(() => {
    if (isFileAwarenessEnabled) {
      openFileAwarenessModal({ clickOrigin: ClickOrigin.MoreInfo, selectedItems: [item] });
    } else {
      openRemoveFilesModal({ clickOrigin: ClickOrigin.MoreInfo });
    }
    trackInfoPanelFileAction(item, ActionName.DeleteForever);
    trackDeleteForeverButtonClicked();
  }, [
    trackInfoPanelFileAction,
    item,
    trackDeleteForeverButtonClicked,
    isFileAwarenessEnabled,
    openFileAwarenessModal,
    openRemoveFilesModal,
  ]);

  const handleRestoreButtonClick = useCallback(() => {
    trackInfoPanelFileAction(item, ActionName.Restore);
    trackRestoreButtonClicked();
    trackRestore([item], { clickOrigin: ClickOrigin.MoreInfo });
    restoreItems();
  }, [trackRestoreButtonClicked, trackRestore, trackInfoPanelFileAction, item, restoreItems]);

  const { trackCreateVideoToolClick } = useTrackCreateVideoToolClick();
  const { openVideoMaker } = useVideoMaker();

  const isVideoFromVideoMaker = canEditItemInVideoMaker(item as MediaManagerSDKFile);
  const canUseItemsInVideoMaker = useCanUseItemsInVideoMaker(item);

  const handleVideoMakerButtonClick = useCallback(() => {
    trackInfoPanelFileAction(item, isVideoFromVideoMaker ? ActionName.ReEdit : ActionName.CreateVideo);
    trackCreateVideoToolClick(isVideoFromVideoMaker ? VideoCreationScript.ReEdit : VideoCreationScript.CreateVideo, [
      item,
    ]);
    openVideoMaker(item as MediaManagerSDKFile);
  }, [trackCreateVideoToolClick, trackInfoPanelFileAction, openVideoMaker, isVideoFromVideoMaker, item]);

  const handleLabelsSave = useCallback((labels) => onLabelsSave(item, labels), [onLabelsSave, item]);

  const canEditImage = canOpenItemInImageEditor(item) && !shouldDisableEditActions;

  const { openModal: openEditBoardModal } = useModalHandlersAndOptions(ModalName.EditBoard);
  const { openModal: openAddFilesToBoardModal } = useModalHandlersAndOptions(ModalName.AddFilesToBoard);

  const { trackBoardActionsClick } = useTrackBoardAnalytics();

  const handleAddFilesToBoardClick = useCallback(() => {
    const clickOrigin = TrackBoardActionsMenuClickOrigin.InfoPanel;

    openAddFilesToBoardModal({ boards: [item], clickOrigin });

    trackBoardActionsClick([item] as MediaManagerSDKBoard[], {
      target: TrackBoardActionsMenuTarget.AddFiles,
      clickOrigin,
    });
  }, [item, openAddFilesToBoardModal, trackBoardActionsClick]);

  const handleEditBoardClick = useCallback(() => {
    openEditBoardModal({ board: item, clickOrigin: ActionClickOrigin.InfoPanel });

    trackBoardActionsClick([item] as MediaManagerSDKBoard[], {
      target: TrackBoardActionsMenuTarget.Edit,
      clickOrigin: TrackBoardActionsMenuClickOrigin.InfoPanel,
    });
  }, [item, openEditBoardModal, trackBoardActionsClick]);

  const shouldShowPermanentDeleteInfo = useShouldShowPermanentDelete(source);

  const infoHeader = (
    <Text weight="bold" size="small" className={styles.fileInfo}>
      <FormattedMessage
        id={
          isBoard(item)
            ? 'panel.bottom-parts.single-item-bottom-part.border-info'
            : isFile(item)
            ? 'panel.bottom-parts.single-item-bottom-part.file-info'
            : 'panel.bottom-parts.single-item-bottom-part.folder-info'
        }
      />
    </Text>
  );

  const infoContent = (
    <>
      {isWixVideoLibraryLink(item as MediaManagerSDKFolder) && (
        <Text weight="thin" size="small" secondary tagName="div" className={styles.info}>
          <FormattedMessage id="panel.bottom-parts.video-library.info" />
        </Text>
      )}

      {isPurchasedFolder(item as MediaManagerSDKFolder) && (
        <>
          <Text weight="thin" size="small" secondary tagName="div" className={styles.info}>
            <FormattedMessage
              id="panel.bottom-parts.single-item-bottom-part.purchased-folder"
              values={{
                shutterStockLink: (
                  <TextButton weight="thin" size="small" as={NavLink} to={RouteBuilder.Source(MainSource.Shutterstock)}>
                    <FormattedMessage id="categories.shutterstock" />
                  </TextButton>
                ),
              }}
            />
          </Text>
        </>
      )}

      {isMobileFolder(item as MediaManagerSDKFolder) && (
        <Text weight="thin" size="small" secondary tagName="div" className={styles.info}>
          <FormattedMessage id="panel.bottom-parts.single-item-bottom-part.mobile-uploads-folder" />
        </Text>
      )}

      <ItemInfoTable item={item} source={source} />
    </>
  );

  const selectedItemsForActions = useMemo(() => [item], [item]);

  const shouldShowTeamInfo = isBoard(item) && team && !disableTeams;
  const userIsBoardOwner = isBoard(item) && isUserOwnerOfBoard(item);

  return (
    <div data-hook="single-item-bottom-part">
      {isBoard(item) ? (
        <BoardInfo name={item.name} description={item.description} />
      ) : (
        <EditingItemName item={item} onRenameComplete={onRenameComplete} />
      )}

      <Actions
        className={styles.actions}
        dataHook="actions"
        selectedItems={selectedItemsForActions}
        isMoreActionsVisible={isMoreActionsVisible}
        contextMenuId={ContextMenuId.SelectedItems}
        source={source}
        onRemoveItemsFromBoard={onRemoveItemsFromBoard}
      >
        {(canEditImage || (canDisplayEditUnsplashAction && !shouldDisableEditActions)) && (
          <ActionItem
            className={styles.actionItem}
            icon={<WixIcons.CropRotate />}
            label={<FormattedMessage id="actions.crop-and-rotate" />}
            onClick={handleCropAndRotateClick}
            dataHook="actions.crop-and-rotate"
          />
        )}

        {canEditImage && (
          <ActionItem
            className={styles.actionItem}
            icon={<WixIcons.Adjust />}
            label={<FormattedMessage id="actions.adjust" />}
            onClick={handleAdjustClick}
            dataHook="actions.adjust"
          />
        )}

        {!isBoards && canUseItemsInVideoMaker && !shouldDisableEditActions && (
          <ActionItem
            className={styles.actionItem}
            icon={<WixIcons.VideoMaker />}
            label={<FormattedMessage id={isVideoFromVideoMaker ? 'actions.edit-video' : 'actions.create-video'} />}
            onClick={handleVideoMakerButtonClick}
            dataHook={isVideoFromVideoMaker ? 'actions.edit-video' : 'actions.create-new-video'}
          />
        )}

        {canEditImage && (
          <ActionItem
            className={styles.actionItem}
            icon={<WixIcons.CutOut />}
            label={<FormattedMessage id="actions.image-background" />}
            onClick={handleCutOutClick}
            dataHook="actions.image-background"
          />
        )}

        {canOpenItemInAdobeExpress(isHideAdobeExpressEnabled)(item) && (
          <ActionItem
            className={styles.actionItem}
            icon={<WixIcons.AdobeExpressLogo />}
            label={<FormattedMessage id="actions.image.adobe-express" />}
            onClick={handleEditInAdobe}
            dataHook="actions.adobe-express"
          />
        )}

        {isRestoreVisible && (
          <ActionItem
            className={styles.actionItem}
            icon={<WixIcons.Revert />}
            label={<FormattedMessage id="actions.restore" />}
            onClick={handleRestoreButtonClick}
            dataHook="actions.restore"
          />
        )}

        {isDeleteForeverVisible && (
          <ActionItem
            className={styles.actionItem}
            icon={<WixIcons.EmptyTrash />}
            label={<FormattedMessage id="actions.delete-forever" />}
            onClick={handleDeleteForeverButtonClick}
            dataHook="actions.delete-forever"
          />
        )}

        {isBoard(item) && (
          <>
            <ActionItem
              className={styles.actionItem}
              icon={<WixIcons.Add />}
              label={<FormattedMessage id="actions.add-files-to-board" />}
              onClick={handleAddFilesToBoardClick}
              dataHook="actions.add-files-to-board"
            />

            {!isSystemBoard(item) && userIsBoardOwner && (
              <ActionItem
                className={styles.actionItem}
                icon={<WixIcons.Edit />}
                label={<FormattedMessage id="actions.edit-board" />}
                onClick={handleEditBoardClick}
                dataHook="actions.edit-board"
              />
            )}
          </>
        )}
      </Actions>

      {shouldShowPermanentDeleteInfo && <DeleteFlowPanelInformation clickOrigin={ClickOriginLearnMore.Trash} />}

      {shouldShowTeamInfo && <Team board={item as MediaManagerSDKBoard} />}

      {isFile(item) && hasLabels(item) && (
        <>
          <LabelsEditor
            readOnly={isTrash}
            item={item}
            labels={item.labels}
            onLabelsSave={handleLabelsSave}
            onLabelClick={handleLabelClick}
            dataHook="single-item-bottom-part-tags-editor"
          />
          <Separator noMargin />
        </>
      )}

      <PanelSection header={infoHeader} collapsed dataHook="file-info">
        {infoContent}
      </PanelSection>
    </div>
  );
};
